@if (incoming(); as call) {
    @let ready = shell.isPickupReady(call);
    @let answering = shell.isPickingUp(call);

    <div
        class="ring-card fixed left-1/2 top-5 z-[85] w-[440px] max-w-[calc(100vw-32px)] -translate-x-1/2"
        role="dialog"
        aria-live="assertive"
        [attr.aria-label]="'voice.call.ring.incoming' | translate">
        <div
            class="relative overflow-hidden rounded-2xl bg-zinc-950 text-white shadow-[0_32px_80px_-28px_rgba(0,0,0,0.65)] ring-1 ring-inset ring-white/10">
            <!-- Atmosphere: emerald glow from the caller's corner, a faint dot weave fading out -->
            <div class="ring-card-mesh pointer-events-none absolute inset-0"></div>
            <div class="ring-card-grid pointer-events-none absolute inset-0"></div>

            <div class="relative flex flex-col gap-4 p-4 pt-3.5">
                <!-- State line -->
                <div class="flex items-center justify-between gap-3">
                    <span
                        class="inline-flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
                        <span class="relative flex size-1.5">
                            <span
                                class="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-80"></span>
                            <span
                                class="relative inline-flex size-1.5 rounded-full bg-emerald-400"></span>
                        </span>
                        {{
                            (call.forMe ? 'voice.call.ring.forYou' : 'voice.call.ring.incoming')
                                | translate
                        }}
                    </span>
                    <span class="font-mono text-[12px] tabular-nums text-zinc-500">
                        {{ waitLabel(call) }}
                    </span>
                </div>

                <!-- Caller -->
                <div class="flex items-center gap-3.5">
                    <div class="relative flex size-14 shrink-0 items-center justify-center">
                        <span
                            class="ring-card-ripple absolute inset-0 rounded-full border border-emerald-400/50"></span>
                        <span
                            class="ring-card-ripple absolute inset-0 rounded-full border border-emerald-400/50 [animation-delay:1.1s]"></span>
                        <span
                            class="relative flex items-center justify-center rounded-full bg-zinc-900 p-[3px] shadow-[0_16px_40px_-14px_rgba(16,185,129,0.6)] ring-1 ring-inset ring-white/10 [&_div]:border-zinc-800">
                            <tw-contact-avatar
                                size="xl"
                                [id]="call.event.conversation_id"
                                [imgUrl]="call.imgUrl"
                                [name]="
                                    call.name || ('voice.call.ring.unknownCaller' | translate)
                                " />
                        </span>
                    </div>

                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                        <span
                            class="truncate text-[17px] font-semibold leading-tight tracking-[-0.015em] text-white">
                            {{ headline(call) || ('voice.call.ring.unknownCaller' | translate) }}
                        </span>
                        <span
                            class="flex min-w-0 items-center gap-2 font-mono text-[12px] text-zinc-400">
                            @if (numberLine(call); as num) {
                                <span class="truncate">{{ num }}</span>
                            }
                            @if (numberLine(call) && call.origin) {
                                <span class="text-zinc-600">·</span>
                            }
                            @if (call.origin) {
                                <span class="truncate">
                                    {{ 'voice.call.ring.via' | translate: { line: call.origin } }}
                                </span>
                            }
                        </span>
                        <div class="mt-1 flex flex-wrap items-center gap-1.5">
                            <!-- Channel chip — WhatsApp vs phone, same pairing as the inbox banner -->
                            <span
                                class="inline-flex items-center gap-1.5 rounded-md px-1.5 py-px text-[11px] font-semibold ring-1 ring-inset"
                                [class]="
                                    call.isWhatsapp
                                        ? 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/30'
                                        : 'bg-white/[0.06] text-zinc-300 ring-white/10'
                                ">
                                @if (call.isWhatsapp) {
                                    <app-whatsapp-icon class="size-3" [origColor]="true" />
                                } @else {
                                    <lucide-icon [img]="PhoneIcon" [size]="11" [strokeWidth]="2" />
                                }
                                {{
                                    (call.isWhatsapp
                                        ? 'voice.call.type.whatsapp'
                                        : 'voice.call.type.phone'
                                    ) | translate
                                }}
                            </span>
                            @if (call.forMe) {
                                <span
                                    class="inline-flex items-center rounded-md bg-emerald-500 px-1.5 py-px text-[11px] font-semibold text-zinc-950">
                                    {{ 'voice.call.queue.forYou' | translate }}
                                </span>
                            } @else if (call.targetName) {
                                <span
                                    class="inline-flex items-center rounded-md bg-white/[0.06] px-1.5 py-px text-[11px] font-medium text-zinc-300 ring-1 ring-inset ring-white/10">
                                    {{
                                        'voice.call.queue.forUser'
                                            | translate: { name: call.targetName }
                                    }}
                                </span>
                            } @else {
                                <span
                                    class="inline-flex items-center rounded-md bg-white/[0.06] px-1.5 py-px text-[11px] font-medium text-zinc-300 ring-1 ring-inset ring-white/10">
                                    {{ 'voice.call.ring.agentQueue' | translate }}
                                </span>
                            }
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-2">
                    <button
                        class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-white/[0.06] px-4 text-[13px] font-semibold text-zinc-200 ring-1 ring-inset ring-white/10 transition-[background-color,transform] duration-200 ease-out-expo hover:bg-white/[0.1] active:scale-[0.98]"
                        type="button"
                        (click)="ignore(call)">
                        <lucide-icon
                            class="text-red-400"
                            [img]="PhoneOffIcon"
                            [size]="15"
                            [strokeWidth]="1.75" />
                        {{ 'voice.call.ring.ignore' | translate }}
                    </button>
                    <button
                        class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 text-[13px] font-semibold text-zinc-950 transition-[background-color,transform,opacity] duration-200 ease-out-expo hover:bg-emerald-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                        [class.ring-card-answer]="ready && !answering"
                        type="button"
                        [disabled]="!ready || answering"
                        (click)="answer(call)">
                        @if (answering || !ready) {
                            <lucide-icon
                                class="animate-spin"
                                [img]="LoaderIcon"
                                [size]="15"
                                [strokeWidth]="2" />
                        } @else {
                            <lucide-icon
                                class="ring-card-answer-icon"
                                [img]="PhoneIcon"
                                [size]="15"
                                [strokeWidth]="2" />
                        }
                        {{
                            (answering
                                ? 'voice.call.ring.answering'
                                : !ready
                                  ? 'voice.call.ring.preparing'
                                  : 'voice.call.ring.answer'
                            ) | translate
                        }}
                    </button>
                </div>
            </div>

            <!-- Others waiting -->
            @if (othersWaiting() > 0) {
                <button
                    class="relative flex w-full items-center justify-between border-t border-white/[0.08] bg-white/[0.03] px-4 py-2 text-left text-[12px] text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
                    type="button"
                    (click)="showQueue()">
                    <span>
                        {{
                            (othersWaiting() === 1
                                ? 'voice.call.ring.moreWaitingOne'
                                : 'voice.call.ring.moreWaiting'
                            ) | translate: { count: othersWaiting() }
                        }}
                    </span>
                    <span class="font-medium text-emerald-300">
                        {{ 'voice.call.ring.showQueue' | translate }}
                    </span>
                </button>
            }
        </div>
    </div>
}
